/**
 * IciciCustomerDetails Component
 * ICICI-specific customer details display with proper formatting for ICICI Direct API response
 * 
 * Exchange Status Code Definitions (Source: ICICI Direct API Documentation):
 * - "C" = CLOSED: Exchange is currently closed (not in trading session)
 * - "Y" = OPEN/ACTIVE: Exchange is currently open and active for trading  
 * - "X" = SUSPENDED/HALTED: Exchange trading is suspended or halted
 * 
 * Data Source: /breezeapi/api/v1/customerdetails endpoint
 * Example Response Structure:
 * {
 *   "exg_status": {
 *     "NSE": "C",  // NSE Closed
 *     "BSE": "C",  // BSE Closed  
 *     "FNO": "Y",  // F&O Open
 *     "NDX": "C"   // NCDEX Closed
 *   }
 * }
 * 
 * Reference: /backend/brokers/india/icici-adapter-service/docs/API_Reference_Docs.md (lines 186-220)
 */

import React from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import {
  Person,
  AccountBox,
  Business,
  DateRange,
  Refresh,
  Info,
  Warning,
  Verified,
  Timeline,
} from "@mui/icons-material";
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const IciciCustomerDetails = ({ 
  account, 
  customerDetails, 
  lastUpdated, 
  onRefresh 
}) => {
  // Extract ICICI customer data structure
  const customerData = customerDetails?.customer_details || customerDetails?.customer_data?.Success || customerDetails?.Success || {};
  
  // Map MUI chip color to DS badge variant (filled)
  const badgeVariant = (color) => {
    switch (color) {
      case "success": return "profit";
      case "error": return "loss";
      case "warning": return "warn";
      case "default":
      default: return "neutral";
    }
  };

  // Helper function to interpret exchange status codes based on ICICI Direct API documentation
  const getExchangeStatus = (statusCode) => {
    switch (statusCode) {
      case "Y": return { text: "Open", color: "success" };
      case "C": return { text: "Closed", color: "default" };
      case "X": return { text: "Suspended", color: "warning" };
      default: return { text: "Unknown", color: "error" };
    }
  };

  // Customer information sections
  const personalInfo = [
    { label: "Last Login", value: customerData.idirect_lastlogin_time || customerData.last_login, icon: DateRange },
    { label: "Order Type", value: customerData.idirect_ORD_TYP, icon: Business },
    { label: "MF Holding Mode", value: customerData.mf_holding_mode_popup_flg === "Y" ? "Enabled" : "Disabled", icon: AccountBox },
  ];

  const accountInfo = [
    { label: "Session Token", value: customerData.session_token ? "Active" : "Inactive" },
    { label: "Last Login", value: customerData.idirect_lastlogin_time },
    { label: "Order Type", value: customerData.idirect_ORD_TYP },
    { label: "MF Holding Mode", value: customerData.mf_holding_mode_popup_flg === "Y" ? "Enabled" : "Disabled" },
  ];

  // Exchange status information grouped together
  const exchangeStatuses = [
    { 
      label: "NSE Status", 
      value: getExchangeStatus(customerData.exg_status?.NSE).text,
      statusColor: getExchangeStatus(customerData.exg_status?.NSE).color,
      rawValue: customerData.exg_status?.NSE
    },
    { 
      label: "BSE Status", 
      value: getExchangeStatus(customerData.exg_status?.BSE).text,
      statusColor: getExchangeStatus(customerData.exg_status?.BSE).color,
      rawValue: customerData.exg_status?.BSE
    },
    { 
      label: "F&O Status", 
      value: getExchangeStatus(customerData.exg_status?.FNO).text,
      statusColor: getExchangeStatus(customerData.exg_status?.FNO).color,
      rawValue: customerData.exg_status?.FNO
    },
    { 
      label: "NCDEX Status", 
      value: getExchangeStatus(customerData.exg_status?.NDX).text,
      statusColor: getExchangeStatus(customerData.exg_status?.NDX).color,
      rawValue: customerData.exg_status?.NDX
    },
    { 
      label: "Commodity Exchange", 
      value: customerData.commodity_exchange_status === "Y" ? "Active" : "Inactive",
      statusColor: customerData.commodity_exchange_status === "Y" ? "success" : "default",
      rawValue: customerData.commodity_exchange_status
    },
  ];

  // Trading segments information
  const tradingSegments = [
    { label: "Commodity Allowed", value: customerData.commodity_allowed === "Y" ? "Yes" : customerData.commodity_allowed === "C" ? "Cash Only" : "No" },
    { label: "Commodity Trade Date", value: customerData.commodity_trade_date },
    { label: "Trading Segment", value: customerData.segments_allowed?.Trading === "Y" ? "Enabled" : "Disabled" },
    { label: "Equity Segment", value: customerData.segments_allowed?.Equity === "Y" ? "Enabled" : "Disabled" },
    { label: "Derivatives Segment", value: customerData.segments_allowed?.Derivatives === "Y" ? "Enabled" : "Disabled" },
  ];

  return (
    <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
      <div className="p-6">
        <div className="space-y-6">
          {/* Header: Identity + Session */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] px-5 py-4 mb-2">
            <div className="flex items-center gap-4 min-w-0">
              <div className="bg-accent rounded-lg p-2 flex items-center justify-center shrink-0">
                <Person className="text-white" sx={{ fontSize: 24 }} />
              </div>
              <div className="min-w-0">
                <Typography variant="h6" className="text-fg-1 font-semibold break-words">
                  {customerData.idirect_user_name || 'N/A'}
                </Typography>
                <Typography variant="body2" className="text-fg-2 break-words">
                  Client ID: {customerData.idirect_userid || customerData.client_code || customerData.user_id || 'N/A'}
                </Typography>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {lastUpdated && (
                <Typography variant="caption" className="text-fg-2">
                  Updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
              )}
              <Tooltip title="Refresh customer details">
                <IconButton
                  onClick={onRefresh}
                  size="small"
                  aria-label="Refresh customer details"
                  className="text-fg-2 hover:text-accent"
                  sx={{
                    '@media (pointer: coarse) and (max-width: 767px)': {
                      minWidth: 44,
                      minHeight: 44,
                    },
                  }}
                >
                  <Refresh sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <span className={`ds-badge sm ${customerData.session_token ? 'profit' : 'neutral'}`}>
                {customerData.session_token ? <Verified sx={{ fontSize: 13 }} /> : <Warning sx={{ fontSize: 13 }} />}
                {customerData.session_token ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Info Grid: Personal + Account Info */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card className="h-full bg-gradient-to-br from-white to-slate-50 dark:from-gray-800 dark:to-gray-700 border border-[var(--divider)]">
                <CardContent>
                  <Typography variant="subtitle1" className="font-semibold text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-2">
                    <Info sx={{ fontSize: 18 }} /> Personal Info
                  </Typography>
                  <div className="space-y-2">
                    {personalInfo.map((item, idx) => item.value && (
                      <div key={idx} className="flex flex-wrap items-start gap-x-2 min-w-0">
                        <item.icon className="text-accent shrink-0 mt-0.5" sx={{ fontSize: 16 }} />
                        <Typography variant="body2" className="text-fg-1 font-medium shrink-0">
                          {item.label}:
                        </Typography>
                        <Typography variant="body2" className="text-fg-2 min-w-0 break-words">
                          {item.value}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className="h-full bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200/50 dark:border-emerald-700/50">
                <CardContent>
                  <Typography variant="subtitle1" className="font-semibold text-emerald-700 dark:text-emerald-300 mb-2 flex items-center gap-2">
                    <Business sx={{ fontSize: 18 }} /> Account Info
                  </Typography>
                  <dl className="space-y-1.5">
                    {accountInfo.map((item, idx) => item.value && (
                      <div key={idx} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0 py-0.5">
                        <dt className="text-fg-2 font-medium text-sm shrink-0">
                          {item.label}
                        </dt>
                        <dd className="text-fg-1 font-medium text-sm min-w-0 break-words">
                          {item.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Exchange Status Section */}
          <Card className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
            <CardContent>
              <Typography variant="subtitle1" className="font-semibold text-indigo-700 dark:text-indigo-300 mb-2 flex items-center gap-2">
                <Business sx={{ fontSize: 18 }} /> Exchange Status
              </Typography>
              <Grid container spacing={2}>
                {exchangeStatuses.map((item, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card className="rounded-lg border border-[var(--divider)] bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-700 dark:to-slate-700">
                      <CardContent className="p-2 flex flex-col items-center">
                        <span className={`ds-badge sm ${badgeVariant(item.statusColor)} mb-1`}>
                          {item.value}
                        </span>
                        <Typography variant="caption" className="text-fg-2 font-mono text-center break-words">
                          {item.label.replace(' Status', '')}
                        </Typography>
                        <Typography variant="caption" className="text-fg-3 mt-1 text-center break-words">
                          Code: {item.rawValue || 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Trading Segments Section */}
          <Card className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
            <CardContent>
              <Typography variant="subtitle1" className="font-semibold text-violet-700 dark:text-violet-300 mb-2 flex items-center gap-2">
                <Timeline sx={{ fontSize: 18 }} /> Trading Segments
              </Typography>
              <Grid container spacing={2}>
                {tradingSegments.map((item, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Card className="rounded-lg border border-[var(--divider)] bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-800 dark:to-gray-800">
                      <CardContent className="p-2 flex flex-col items-start">
                        <Typography variant="body2" className="text-fg-1 font-semibold mb-1">
                          {item.label}
                        </Typography>
                        <span className="ds-badge sm role">
                          {item.value}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Status Code Reference (Accordion) */}
          <Accordion className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] mt-2">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" className="font-semibold text-cyan-700 dark:text-cyan-300 flex items-center gap-2">
                <Info sx={{ fontSize: 16 }} /> ICICI Exchange Status Code Reference
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Card className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20">
                    <CardContent className="p-2 flex flex-col items-center">
                      <span className="ds-badge sm profit mb-1">Open</span>
                      <Typography variant="caption" className="text-fg-1 font-medium mb-1">Code: Y</Typography>
                      <Typography variant="caption" className="text-fg-2">Exchange is open for trading</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card className="rounded-lg border border-[var(--divider)] bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-700 dark:to-slate-700">
                    <CardContent className="p-2 flex flex-col items-center">
                      <span className="ds-badge sm neutral mb-1">Closed</span>
                      <Typography variant="caption" className="text-fg-1 font-medium mb-1">Code: C</Typography>
                      <Typography variant="caption" className="text-fg-2">Exchange is currently closed</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card className="rounded-lg border border-orange-200 dark:border-orange-700 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20">
                    <CardContent className="p-2 flex flex-col items-center">
                      <span className="ds-badge sm warn mb-1">Suspended</span>
                      <Typography variant="caption" className="text-fg-1 font-medium mb-1">Code: X</Typography>
                      <Typography variant="caption" className="text-fg-2">Trading is suspended or halted</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <div className="mt-3 p-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <Typography variant="caption" className="text-blue-700 dark:text-blue-300 block font-medium">
                  📘 Status codes based on ICICI Direct API documentation
                </Typography>
                <Typography variant="caption" className="text-blue-600 dark:text-blue-400 block mt-0.5">
                  Updates every 30 seconds during market hours
                </Typography>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Data Source/Session Alert */}
          <div className="mt-2">
            <Card className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg shadow-md border-0 overflow-hidden">
              <CardContent className="p-3 flex items-center gap-3">
                <Info className="text-white" sx={{ fontSize: 20 }} />
                <div>
                  <Typography variant="body2" className="text-white font-semibold mb-1">
                    Data Source: ICICI Direct API (real-time)
                  </Typography>
                  <Typography variant="caption" className="text-blue-100 leading-relaxed">
                    Customer data is retrieved directly from ICICI Direct API. Live data connection {customerData.session_token ? 'active' : 'inactive'}.
                  </Typography>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IciciCustomerDetails;
